import { defaultCheckError, IRequestDataAction, progressEpic } from "epics/base-epic";
import { updateImport, updateExport } from "actions/size-packs-actions";
import { SIZE_PACKS } from "../../constants/action-types";
import siteConfig from "../../site-config";
import { IFetchJobActionPayload, IUploadResponse } from "./uploaders";
import { JOB_POLL_INTERVAL, jobEndStatuses, jobEndSuccessStatuses, jobEndFailureStatuses } from "../../constants/vetl";

import { Observable } from "rxjs";

type IExportResponse = {
    message: string;
    localizedMessage?: string;
    job?: any;
    errors?: string[];
};

type IExportFileResponse = {
    message: string;
    localizedMessage?: string;
    fileLink: string;
    errors?: string[];
};

export const uploadSizePacks = progressEpic<IRequestDataAction, null>(
    SIZE_PACKS.UPLOAD,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + payload.url,
        body: payload.body,
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            module: "default",
            Accept: "application/json",
        },
    }),
    {
        dataCustomizer: () => null,
        checkErrorStatus: defaultCheckError,
        finishActions: () => [updateImport({ uploadFinished: true })],
    },
);

export const sizePacksExportEpic = progressEpic<IRequestDataAction, IExportResponse>(
    SIZE_PACKS.EXPORT.DOWNLOAD,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + payload.url,
        body: payload.body,
    }),
    {
        dataCustomizer: (response: IExportResponse) => ({
            errors: response.errors,
            message: response.localizedMessage,
            job: response.job,
        }),
        checkErrorStatus: defaultCheckError,
        finishActions: (response) => [
            { type: SIZE_PACKS.EXPORT.PROCESS.START },
            fetchSizePacksExportJob(response.job.id, response.job.pipelineId),
        ],
    },
);

export const sizePacksExportFileEpic = progressEpic<IRequestDataAction, IExportFileResponse>(
    SIZE_PACKS.EXPORT_FILE.FETCH,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + payload.url,
        body: payload.body,
    }),
    {
        dataCustomizer: (response: IExportFileResponse) => ({
            errors: response.errors,
            message: response.localizedMessage,
            fileLink: response.fileLink,
        }),
        checkErrorStatus: defaultCheckError,
    },
);

const fetchSizePacksExportJob = (jobId: string, pipelineId: string) => ({
    type: SIZE_PACKS.EXPORT.JOB.FETCH.START,
    payload: { url: "/api/v3.0/vetl/job", body: { jobId, pipelineId } },
});

const startSizePacksExportJobPolling = (payload) => ({ type: SIZE_PACKS.EXPORT.JOB.POLL.START, payload });

export const fetchSizePacksExportJobEpic = progressEpic<IFetchJobActionPayload, IUploadResponse>(
    SIZE_PACKS.EXPORT.JOB.FETCH,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + payload.url,
        body: payload.body,
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            module: "default",
        },
    }),
    {
        dataCustomizer: (response: IUploadResponse) => {
            return { errors: response.errors, message: response.localizedMessage, job: response.job };
        },
        checkErrorStatus: defaultCheckError,
        finishActions: (response) => {
            if (!jobEndStatuses.includes(response.job.status)) {
                return [
                    startSizePacksExportJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId }),
                ];
            }

            if (jobEndSuccessStatuses.includes(response.job.status)) {
                return [{ type: SIZE_PACKS.EXPORT.PROCESS.SUCCESS }];
            }

            if (jobEndFailureStatuses.includes(response.job.status)) {
                return [{ type: SIZE_PACKS.EXPORT.PROCESS.ERROR }];
            }
        },
    },
);

export const pollSizePacksExportJobEpic = (action$) =>
    action$.ofType(SIZE_PACKS.EXPORT.JOB.POLL.START).mergeMap(({ payload }) =>
        Observable.timer(JOB_POLL_INTERVAL)
            .takeUntil(action$.ofType(SIZE_PACKS.EXPORT.JOB.POLL.STOP))
            .flatMap(() => [fetchSizePacksExportJob(payload.jobId, payload.pipelineId)]),
    );

export const startWatingForDownloadLinkEpic = (action$) =>
    action$.ofType(SIZE_PACKS.EXPORT.DOWNLOAD.START).mapTo(updateExport({ isWaitingForDownloadLink: true }));

export const stopWatingForDownloadLinkEpic = (action$) =>
    action$
        .ofType(
            SIZE_PACKS.EXPORT.DOWNLOAD.ERROR,
            SIZE_PACKS.EXPORT_FILE.FETCH.ERROR,
            SIZE_PACKS.EXPORT.PROCESS.ERROR,
            SIZE_PACKS.EXPORT_FILE.FETCH.SUCCESS,
        )
        .mapTo(updateExport({ isWaitingForDownloadLink: false }));
